import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { useAnalytics } from '@features/Analytics';
import {
  stripePaymentRetryAtom,
  stripeTryAnotherCardAtom,
  userAtom,
  userPaymentsInfoAtom,
} from '@features/Stripe/atoms';

type TpTryAnotherCardParams = {
  paymentMethod: string;
  planId: string;
};

type TpTryAnotherCardAnalyticsParams = TpTryAnotherCardParams & {
  tryAnotherCardShownCount: number;
  tryAnotherCardTappedCount: number;
};

export const useStripeTryAnotherCard = () => {
  const [tryAnotherCardState, setTryAnotherCardState] = useAtom(
    stripeTryAnotherCardAtom,
  );
  const setPaymentRetry = useSetAtom(stripePaymentRetryAtom);
  const { subscriptionId } = useAtomValue(userPaymentsInfoAtom);
  const { userId } = useAtomValue(userAtom);
  const { trackGoogleEvent } = useAnalytics();

  const getAnalyticsOptions = useCallback(
    ({
      paymentMethod,
      planId,
      tryAnotherCardShownCount,
      tryAnotherCardTappedCount,
    }: TpTryAnotherCardAnalyticsParams) => ({
      payment_method: paymentMethod,
      plan_id: planId,
      subscription_id: subscriptionId,
      try_another_card_shown_count: tryAnotherCardShownCount,
      try_another_card_taped_count: tryAnotherCardTappedCount,
      user_id: userId,
    }),
    [subscriptionId, userId],
  );

  const showTryAnotherCardModal = useCallback(
    ({ paymentMethod, planId }: TpTryAnotherCardParams) => {
      let analyticsParams: TpTryAnotherCardAnalyticsParams = {
        paymentMethod,
        planId,
        tryAnotherCardShownCount:
          tryAnotherCardState.tryAnotherCardModalShownCount + 1,
        tryAnotherCardTappedCount:
          tryAnotherCardState.tryAnotherCardTappedCount,
      };

      setTryAnotherCardState((previous) => {
        analyticsParams = {
          paymentMethod,
          planId,
          tryAnotherCardShownCount: previous.tryAnotherCardModalShownCount + 1,
          tryAnotherCardTappedCount: previous.tryAnotherCardTappedCount,
        };

        return {
          ...previous,
          showModal: true,
          planId,
          paymentMethod,
          tryAnotherCardModalShownCount:
            analyticsParams.tryAnotherCardShownCount,
        };
      });

      trackGoogleEvent({
        eventName: 'try_another_card_shown',
        options: getAnalyticsOptions(analyticsParams),
      });
    },
    [
      getAnalyticsOptions,
      setTryAnotherCardState,
      trackGoogleEvent,
      tryAnotherCardState.tryAnotherCardModalShownCount,
      tryAnotherCardState.tryAnotherCardTappedCount,
    ],
  );

  const hideTryAnotherCardModal = useCallback(() => {
    setTryAnotherCardState((previous) => ({
      ...previous,
      showModal: false,
    }));
  }, [setTryAnotherCardState]);

  const handleTryAnotherCardAction = useCallback(() => {
    let analyticsParams: TpTryAnotherCardAnalyticsParams = {
      paymentMethod: tryAnotherCardState.paymentMethod,
      planId: tryAnotherCardState.planId,
      tryAnotherCardShownCount:
        tryAnotherCardState.tryAnotherCardModalShownCount,
      tryAnotherCardTappedCount:
        tryAnotherCardState.tryAnotherCardTappedCount + 1,
    };

    setTryAnotherCardState((previous) => {
      analyticsParams = {
        paymentMethod: previous.paymentMethod,
        planId: previous.planId,
        tryAnotherCardShownCount: previous.tryAnotherCardModalShownCount,
        tryAnotherCardTappedCount: previous.tryAnotherCardTappedCount + 1,
      };

      return {
        ...previous,
        showModal: false,
        tryAnotherCardTappedCount: analyticsParams.tryAnotherCardTappedCount,
        clearCardFormCount: previous.clearCardFormCount + 1,
      };
    });
    setPaymentRetry((previous) => ({
      ...previous,
      retryPaymentErrorKey: '',
      sameRetryablePaymentErrorRetryCount: 0,
    }));

    trackGoogleEvent({
      eventName: 'try_another_card_taped',
      options: getAnalyticsOptions(analyticsParams),
    });
  }, [
    getAnalyticsOptions,
    setPaymentRetry,
    setTryAnotherCardState,
    trackGoogleEvent,
    tryAnotherCardState.paymentMethod,
    tryAnotherCardState.planId,
    tryAnotherCardState.tryAnotherCardModalShownCount,
    tryAnotherCardState.tryAnotherCardTappedCount,
  ]);

  return {
    ...tryAnotherCardState,
    handleTryAnotherCardAction,
    hideTryAnotherCardModal,
    showTryAnotherCardModal,
  };
};
